/**
 * WebForm Component.
 * Dynamic Form will be generated as per Response from API.
 * The `WebForm` Component internally calls form fields components.
 * Field Component located in `public/js/contactUs/components/form`.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Form from 'kepler/lib/components/Form';
import _get from 'lodash/get';
import _findIndex from 'lodash/findIndex';
import _filter from 'lodash/filter';
import _isEmpty from 'lodash/isEmpty';

// Components and Shapes.
import { Link } from './index';
import { TextBox, SelectBox, RadioGroup, TextArea, DateSelector, Notes, FormButtons } from '../form/index';
import { validEmailType } from '../../../lib/emailUtil';
import { WEBFORM_ERRORS, WEBFORM_FIELDS_DETAILS } from '../../constants/PropTypesShapes';

class WebForm extends Component {
  // Set State to Validate all the Field and Field Configuration Field.
  constructor(props) {
    super(props);

    this.state = {
      validateField: false,
      fields: props.fields,
    };

    // Binding the Prop Methods.
    this.setFieldValue = this.setFieldValue.bind(this);
  }

  // Focus the First Field, after Component rendered.
  componentDidMount() {
    const firstFieldId = _get(this.state, 'fields[0].id', '');

    if (firstFieldId && document.getElementById(firstFieldId)) {
      document.getElementById(firstFieldId).focus();
    }
  }

  // Props Value will be supplied after every Topic Tree Changes.
  componentWillReceiveProps(props) {
    this.setState({
      fields: props.fields,
    });
  }

  // Handle the Form Validation and Submission.
  // Submit the Form, if there is no error.
  // Check if there is any errors in Form Fields to Trigger all the Form Field Validation to show the erros.
  handleFormValidate(e) {
    e.preventDefault();

    // Filter out the field which has dependency but value of dependency field is null; field will not show, if the dependency field is null
    const displayedFields = _filter(this.state.fields, (field) => {
      if (!field.dependsOnId) {
        return true;
      }

      return !this.dependsFieldNoValue(field);
    });

    const errorFields = _filter(displayedFields, (field) => {
      const nonFields = ['paragraph', 'anchor'];
      return field.hasError === true && nonFields.indexOf(field.type) === -1;
    });

    if (_isEmpty(errorFields)) {
      this.props.handleSubmit(displayedFields);
    } else {
      this.setState({
        validateField: true,
      });
    }
  }

  // Set the Field Value to Form State data.
  // Preserve the Field Value.
  setFieldValue(field) {
    const fieldIndex = _findIndex(this.state.fields, { id: field.id });
    let formData = this.state.fields;

    if (formData[fieldIndex]) {
      if (field.value) {
        formData[fieldIndex].value = field.value;
      }

      if (formData[fieldIndex].hasError) {
        formData[fieldIndex].hasError = field.hasError;
      }
    }

    // Reset form fields which has dependency field
    formData = formData.map((form) => {
      // not reset form, if it dose not have the dependency field or dependency field is no changed and still has value
      if (!form.dependsOnId || (form.dependsOnId !== field.id && !this.dependsFieldNoValue(form))) {
        return form;
      }
      return { ...form, value: '', hasError: form.isRequired && validEmailType.indexOf(form.type) >= 0 };
    });

    this.setState({
      fields: formData,
    });

    this.props.handleFieldValue(formData);

    if (this.props.submitOnFieldChange) {
      this.props.handleSubmit(this.state.fields);
    }
  }

  // Filter the Field Value By Field Id to get the Value.
  // eslint-disable-next-line react/no-unused-class-component-methods
  getFieldValue(fieldId) {
    let fieldValue = '';
    const fieldIndex = _findIndex(this.state.fields, { id: fieldId });

    if (this.state.fields[fieldIndex]) {
      fieldValue = this.state.fields[fieldIndex].value;
    }

    return fieldValue;
  }

  // Return the Field Component as per `Field.Type`.
  // Text and Number are same currently.
  getField(field) {
    // Not show Email field, if the field have dependency but the dependency field value is null/undefined
    if (field.dependsOnId && this.dependsFieldNoValue(field)) {
      return null;
    }

    switch (field.type) {
      case 'text':
      case 'number':
        return (
          <TextBox
            key={field.id}
            options={field}
            validate={this.state.validateField}
            handleValue={this.setFieldValue}
            errorWarn={this.props.errors}
          />
        );
      case 'date':
        return (
          <DateSelector
            key={field.id}
            options={field}
            validate={this.state.validateField}
            handleValue={this.setFieldValue}
            errorWarn={this.props.errors}
          />
        );
      case 'textarea':
        return (
          <TextArea
            key={field.id}
            options={field}
            validate={this.state.validateField}
            handleValue={this.setFieldValue}
            errorWarn={this.props.errors}
          />
        );
      case 'select':
        return (
          <SelectBox
            key={field.id}
            options={field}
            validate={this.state.validateField}
            handleValue={this.setFieldValue}
            errorWarn={this.props.errors}
          />
        );
      case 'radio':
        return <RadioGroup key={field.id} options={field} handleValue={this.setFieldValue} />;
      case 'paragraph':
        return <Notes key={field.id} label={field.label} />;
      case 'anchor':
        return (
          <Link
            classNames="link-field"
            key={field.id}
            id={field.id}
            href={field.href}
            label={field.title}
            title={field.label}
            target="_blank"
          />
        );
      case 'button':
        return (
          <FormButtons
            submitLabel={field.submitLabel}
            resetLabel={field.resetLabel}
            hasCancelButton={field.hasCancelButton}
            submitButtonStyle={field.submitButtonStyle}
            handleCancel={field.handleCancel}
          />
        );
      default:
        return <div />;
    }
  }

  // Iterate each Form Fields from State.
  getFormFields() {
    return this.state.fields.map((field) => this.getField(field));
  }

  // Return Form Submit and Cancel Buttons, if Submit Label exist.
  getFormButtons() {
    if (this.props.submitLabel) {
      return (
        <FormButtons
          submitLabel={this.props.submitLabel}
          resetLabel={this.props.resetLabel}
          handleCancel={this.props.handleCancel}
          submitButtonStyle={this.props.submitButtonStyle}
          cancelButtonStyle={this.props.cancelButtonStyle}
          hasCancelButton={this.props.hasCancelButton}
        />
      );
    }
    return null;
  }

  getDependsFieldIndex(field) {
    return _findIndex(this.state.fields, { id: field.dependsOnId });
  }

  getDependsFieldValue(field) {
    if (!field.dependsOnId) {
      return null;
    }

    const fieldIndex = this.getDependsFieldIndex(field);
    return this.state.fields[fieldIndex].value;
  }

  // Check the dependency field whether has data or not
  dependsFieldNoValue(field) {
    const dependsFieldValue = this.getDependsFieldValue(field);

    // field only dependsOnId without value dependency
    if (!Array.isArray(field.dependsOnValue) || !field.dependsOnValue.length) {
      return !dependsFieldValue; // return true if the dependency field is empty value, so that not show the field
    }

    return field.dependsOnValue.indexOf(dependsFieldValue) < 0;
  }

  // Render the `WebForm` with Fields, Submit and Cancel Buttons.
  render() {
    return (
      <div className="webform">
        <Form onSubmit={(e) => this.handleFormValidate(e)}>
          <fieldset>
            {this.props.title !== '' && <legend>{this.props.title}</legend>}
            {this.getFormFields()}
            {this.getFormButtons()}
          </fieldset>
        </Form>
      </div>
    );
  }
}

// WebForm Props Types.
WebForm.propTypes = {
  // Simple Prop Types.
  title: PropTypes.string,
  fields: PropTypes.arrayOf(PropTypes.shape(WEBFORM_FIELDS_DETAILS)),
  submitOnFieldChange: PropTypes.bool,
  submitLabel: PropTypes.string,
  resetLabel: PropTypes.string,
  errors: PropTypes.shape(WEBFORM_ERRORS),
  // Method Prop Types.
  handleFieldValue: PropTypes.func.isRequired,
  handleCancel: PropTypes.func,
  handleSubmit: PropTypes.func.isRequired,
  submitButtonStyle: PropTypes.string,
  cancelButtonStyle: PropTypes.string,
  hasCancelButton: PropTypes.bool,
};

// WebForm Props default values.
WebForm.defaultProps = {
  title: '',
  fields: [],
  submitOnFieldChange: false,
  submitLabel: '',
  resetLabel: '',
  errors: {},
  handleCancel: () => {},
  submitButtonStyle: 'left-col col-xs-6 col-sm-6 col-md-4 col-lg-3',
  cancelButtonStyle: 'right-col col-xs-6 col-sm-6 col-md-4 col-lg-3',
  hasCancelButton: true,
};

export default WebForm;
